import { useEffect, useRef, useState } from 'react';
import { EditingActions, InputField } from '../UI';
import { TREATMENT_PLAN_NAME_MAX_LENGTH } from '../../constants/treatmentPlanConstants';
import { normalizeTreatmentPlanName } from '../../services/treatmentPlanNameService';
import styles from './GanttGroupLabel.module.css';

interface GanttGroupLabelInputProps {
  initialName: string;
  ariaLabel: string;
  onCommit: (name: string) => void;
  onCancel: () => void;
}

export const GanttGroupLabelInput = ({
  initialName,
  ariaLabel,
  onCommit,
  onCancel
}: GanttGroupLabelInputProps) => {
  const [value, setValue] = useState(initialName);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.select();
  }, []);

  // Asks the same rule the year-plan edit asks, so the save button can never
  // offer a name the hook would then refuse. Disabling save is how a row is
  // kept from going nameless, matching the settings tables' editable cells.
  const validName = normalizeTreatmentPlanName(value);
  const canSave = validName !== null;

  const save = (): void => {
    if (validName !== null) onCommit(validName);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLButtonElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      save();
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      onCancel();
    }
  };

  return (
    <>
      <InputField
        className={styles.renameField}
        inputRef={inputRef}
        value={value}
        ariaLabel={ariaLabel}
        maxLength={TREATMENT_PLAN_NAME_MAX_LENGTH}
        onChange={(e) => {
          setValue(e.target.value);
        }}
        onKeyDown={handleKeyDown}
      />
      <EditingActions canSave={canSave} onSave={save} onCancel={onCancel} />
    </>
  );
};
